import React, { useEffect, useState } from "react";
import { Card, CardBody, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { BlogService } from "./service";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import DeleteModal from "../../Common/DeleteModal";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import Pagination from "../../Common/Pagination";

const BlogList = () => {
  const { t } = useTranslation(); // Initialize translation hook
  const [list, setList] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [term, setTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [blogToDelete, setBlogToDelete] = useState<number | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const navigate = useNavigate();

  const entriesPerPageOptions = [
    { label: "5", value: 5 },
    { label: "10", value: 10 },
    { label: "20", value: 20 },
    { label: "30", value: 30 },
    { label: "40", value: 40 },
  ];

  const handleTermChange = (e: any) => {
    setTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleEntriesPerPageChange = (selectedOption: any) => {
    setEntriesPerPage(selectedOption ? selectedOption.value : 10);
    setCurrentPage(1);
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleDeleteClick = (id: number) => {
    setBlogToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (blogToDelete !== null) {
      try {
        await BlogService.delete(blogToDelete);
        SuccessToast(t("blog.successDelete"));
        setShowDeleteModal(false);
        setBlogToDelete(null);
        fetchData();
      } catch (error) {
        ErrorToast(error);
      }
    }
  };

  const normalizePagination = (response: any) => {
    const data =
      response?.data?.data ?? response?.data ?? response?.items ?? [];

    const totalPagesRaw =
      response?.totalPages ??
      response?.data?.totalPages ??
      response?.meta?.totalPages ??
      response?.pagination?.totalPages ??
      response?.total_pages ??
      0;

    const totalRaw =
      response?.total ??
      response?.data?.total ??
      response?.meta?.total ??
      response?.pagination?.total ??
      0;

    const perPageRaw =
      response?.perPage ??
      response?.data?.perPage ??
      response?.meta?.perPage ??
      response?.pagination?.perPage ??
      entriesPerPage;

    const totalPages =
      Number(totalPagesRaw) ||
      (Number(totalRaw) && Number(perPageRaw)
        ? Math.ceil(Number(totalRaw) / Number(perPageRaw))
        : 0);

    return {
      data: Array.isArray(data) ? data : [],
      totalPages,
    };
  };

  const handleCategoryFilterChange = (category: string | null) => {
    setCategoryFilter(category);
    setCurrentPage(1);
  };

  const fetchData = async () => {
    try {
      const response = await BlogService.getAll({
        page: currentPage,
        perPage: entriesPerPage,
        term,
        ...(categoryFilter ? { category: categoryFilter } : {}),
      });
      const normalized = normalizePagination(response);
      setList(normalized.data);
      setTotalPages(normalized.totalPages);
    } catch (error) {
      ErrorToast(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, entriesPerPage, term, categoryFilter]);

  return (
    <React.Fragment>
      <Col>
        <Card>
          <CardBody className="card-body">
            <div className="text-end">
              <button
                className="btn btn-primary text-end"
                onClick={() => navigate("/blogs/create")}
              >
                <i className="ti ti-plus f-18"></i> {t("blog.add")}
              </button>
            </div>

            <div className="d-sm-flex align-items-center mb-3">
              <ul className="list-inline me-auto my-1">
                <li className="list-inline-item">
                  <Select
                    placeholder={t("blog.entriesPerPage")}
                    options={entriesPerPageOptions}
                    onChange={handleEntriesPerPageChange}
                    defaultValue={entriesPerPageOptions.find(
                      (option) => option.value === entriesPerPage,
                    )}
                    classNamePrefix="react-select"
                  />
                </li>
                <span> {t("blog.entriesPerPage")}</span>
              </ul>
              <ul className="list-inline ms-auto my-1 mt-3">
                <li className="list-inline-item">
                  <input
                    type="search"
                    className="form-control"
                    placeholder={t("blog.searchPlaceholder")}
                    onChange={handleTermChange}
                  />
                </li>
              </ul>
            </div>
            <div className="mb-3">
              <div className="btn-group" role="group">
                <button
                  type="button"
                  className={`btn btn-sm ${categoryFilter === null ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => handleCategoryFilterChange(null)}
                >
                  {t("blog.all")}
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${categoryFilter === "blog" ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => handleCategoryFilterChange("blog")}
                >
                  {t("blog.categoryBlog")}
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${categoryFilter === "podcast" ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => handleCategoryFilterChange("podcast")}
                >
                  {t("blog.categoryPodcast")}
                </button>
              </div>
            </div>
            <div className="table-responsive">
              <table className="table table-hover sticky-actions" id="pc-dt-simple-1">
                <thead>
                  <tr>
                    <th>{t("blog.id")}</th>
                    <th>{t("blog.title")}</th>
                    <th>{t("blog.category")}</th>
                    <th>{t("blog.createdAt")}</th>
                    <th>{t("blog.updatedAt")}</th>
                    <th className="text-end sticky-col">{t("blog.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((item: any, index: number) => (
                    <tr key={index}>
                      <td>{item.id}</td>
                      <td className="wrap-col">{item.title}</td>
                      <td>
                        {item.category ? (
                          <span
                            className={`badge ${item.category === "podcast" ? "bg-info" : "bg-primary"}`}
                          >
                            {t(
                              `blog.category${item.category.charAt(0).toUpperCase() + item.category.slice(1)}`,
                            )}
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>{new Date(item.createdAt).toLocaleDateString()}</td>
                      <td>{new Date(item.updatedAt).toLocaleDateString()}</td>
                      <td className="text-end text-nowrap sticky-col">
                        <ul className="list-inline mb-0">
                          <li
                            className="list-inline-item"
                            onClick={() => navigate(`/blogs/edit/${item.id}`)}
                          >
                            <a
                              href="#"
                              className="avtar avtar-s btn-link-success btn-pc-default"
                            >
                              <i className="ti ti-edit f-20"></i>
                            </a>
                          </li>
                          <li className="list-inline-item">
                            <span
                              role="button"
                              className="avtar avtar-s btn-link-danger btn-pc-default"
                              onClick={() => handleDeleteClick(item.id)}
                            >
                              <i className="ti ti-trash f-20"></i>
                            </span>
                          </li>
                        </ul>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              previousLabel={t("blog.previous")}
              nextLabel={t("blog.next")}
            />
          </CardBody>
        </Card>
      </Col>

      <DeleteModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        handleDeleteId={handleDeleteConfirm}
        name={t("blog.name")}
      />
    </React.Fragment>
  );
};

export default BlogList;
